import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Sparkles, ArrowRight, MessageCircle, Download } from 'lucide-react';
import { brand } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const paymentId = params.get('payment');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API}/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  const waLink = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(
    `Hi EC Safetrade, I just completed my payment. Order: ${orderId} Payment: ${paymentId}. Please confirm next steps.`
  )}`;

  return (
    <section className="pt-32 pb-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #E8F5EE 0%, #ffffff 60%)' }}>
      <div className="blob" style={{ background: '#1F8B4C', width: 380, height: 380, top: -100, left: -100 }} />
      <div className="blob" style={{ background: '#FF7A00', width: 320, height: 320, bottom: -100, right: -100, opacity: 0.2 }} />

      <div className="container-x relative z-10 max-w-3xl">
        <div className="bg-white rounded-3xl shadow-2xl border border-neutral-100 p-8 md:p-12 text-center">
          <div className="mx-auto w-24 h-24 rounded-full bg-[color:var(--brand-green-light)] flex items-center justify-center mb-6 relative">
            <CheckCircle2 size={54} className="text-[color:var(--brand-green)]" />
            <Sparkles size={20} className="absolute -top-2 -right-2 text-[color:var(--brand-orange)]" />
          </div>

          <div className="inline-flex items-center gap-2 bg-[color:var(--brand-green-light)] text-[color:var(--brand-green-dark)] text-[11px] font-bold uppercase tracking-[3px] px-4 py-1.5 rounded-full mb-4">
            <Sparkles size={12} /> Payment successful
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-[color:var(--ink)] leading-tight">
            🎉 Congratulations!
          </h1>
          <p className="mt-3 text-lg text-[color:var(--ink-soft)] max-w-xl mx-auto">
            Thank you for choosing <span className="font-bold text-[color:var(--brand-green)]">EC Safetrade Solutions</span>. Your subscription is confirmed and our team will reach out within <b>24 hours</b> to kick things off.
          </p>

          {order && (
            <div className="mt-8 bg-[color:var(--brand-green-light)]/40 rounded-2xl p-5 text-left max-w-md mx-auto border border-[color:var(--brand-green)]/15">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--ink-soft)]">Plan</div>
                  <div className="font-bold text-[color:var(--ink)]">{order.plan_name}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--ink-soft)]">Duration</div>
                  <div className="font-bold text-[color:var(--ink)]">{order.duration}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--ink-soft)]">Amount Paid</div>
                  <div className="font-black text-[color:var(--brand-green)]">₹{Number(order.amount_rupees).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--ink-soft)]">Status</div>
                  <div className="font-bold text-[color:var(--brand-green)] capitalize">{order.status}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[color:var(--brand-green)]/20 text-[11px] font-mono text-[color:var(--ink-soft)] break-all">
                <div>Order ID: {orderId}</div>
                <div>Payment ID: {paymentId}</div>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-full text-sm hover:opacity-90 transition">
              <MessageCircle size={16} /> Confirm on WhatsApp
            </a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 bg-[color:var(--brand-green)] text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-[color:var(--brand-green-dark)] transition">
              Back to Home <ArrowRight size={16} />
            </Link>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center justify-center gap-2 bg-white text-[color:var(--ink)] font-semibold px-6 py-3 rounded-full text-sm border-2 border-neutral-200 hover:border-[color:var(--brand-green)] hover:text-[color:var(--brand-green)] transition"
            >
              <Download size={16} /> Save Receipt
            </button>
          </div>

          <p className="mt-8 text-xs text-[color:var(--ink-soft)]">
            Need help? Call <a href={`tel:${brand.phoneRaw}`} className="text-[color:var(--brand-green)] font-semibold">{brand.phone}</a>{' '}or email <a href={`mailto:${brand.email}`} className="text-[color:var(--brand-green)] font-semibold">{brand.email}</a>
          </p>
        </div>
      </div>
    </section>
  );
}
